/**
 * Every FI-facing word the Jira tab renders, plus its URL keys. No copy lives in
 * a component.
 *
 * Status LABELS are not here — they come from `GET /api/admin/jira/status-map`
 * (see `common/issueStatusPill/issueStatus.constants`), because the FI-facing
 * prose for a status is the server's to change. What is here is the screen's own
 * copy: the sync banner, the empty states and the filter chips.
 *
 * Pure: no SCSS or JSX imports, so any consumer (including a test) can import it
 * safely — the same rule `support.enums.js` states for itself.
 */

import { SCOPES } from '../../constants/scopes.constants';

/** Filter value meaning "no status filter" — never sent to the API, never written to the URL. */
export const ISSUE_STATUS_FILTER_ALL = 'all';

/**
 * URL key for the Jira tab's status chips.
 *
 * Deliberately NOT `status`: the support-tickets tab already owns that key for
 * its own five-status tab row, and the two tabs share one query string. `page`
 * and `perPage` are shared too (the Table writes them), which is why switching
 * tabs clears `page`.
 */
export const ISSUE_STATUS_PARAM = 'issueStatus';

/**
 * URL key for the table/kanban toggle, so a view survives a refresh and can be
 * shared by link — the same rule the status chips follow.
 */
export const ISSUE_VIEW_PARAM = 'view';
export const ISSUE_VIEWS = Object.freeze({ TABLE: 'table', KANBAN: 'kanban' });
/**
 * The board is the default. The question this screen answers first is "what is
 * the team working on", which is a shape — how much sits in each status — not a
 * list of rows. The table is there for reading detail and exact timestamps.
 */
export const ISSUE_VIEW_DEFAULT = ISSUE_VIEWS.KANBAN;

/** Tab key for the Jira tab within the support screen. */
export const SUPPORT_TAB_PARAM = 'tab';
export const SUPPORT_TABS = Object.freeze({ TICKETS: 'tickets', ISSUES: 'issues' });
export const SUPPORT_TAB_DEFAULT = SUPPORT_TABS.TICKETS;

/**
 * Rows the TABLE asks the server for per page.
 *
 * `GET /api/admin/jira/:aba/issues` is server-paginated in both views, so this
 * is a request parameter, not a local slice: `rows` is always exactly one page
 * and `pageinfo.count` is what antd draws the pager from.
 *
 * Note the screen opens on whatever `?perPage=` the URL carries — the key is
 * shared with the tickets tab — so this is the default, not a ceiling.
 */
export const ISSUES_PER_PAGE = 10;

/**
 * Rows fetched per board-column request — the same page size as the table, so
 * "a page" means one thing on this screen no matter which view you are in.
 */
export const ISSUES_COLUMN_PAGE_SIZE = ISSUES_PER_PAGE;

/**
 * A board column before anything has been loaded into it.
 *
 * Re-exported from design-elements rather than declared here: the board this
 * screen draws is `KanbanBoard`, and the empty shape is part of ITS contract —
 * a local copy would silently drift the day the component adds a field.
 */
export { EMPTY_KANBAN_COLUMN as EMPTY_ISSUE_COLUMN } from '@clockout-dev/design-elements';

/**
 * Chip options. Only the three statuses the story asks the FI to slice by get a
 * chip; `closed` and `unmapped` still appear under "All", so no row is ever
 * unreachable — an unmapped issue must be visible, not filtered out of existence.
 */
export const ISSUE_STATUS_CHIPS = Object.freeze([
    { key: ISSUE_STATUS_FILTER_ALL, label: 'All' },
    { key: 'queued', label: 'Queued' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'done', label: 'Done' },
]);

export const ISSUE_TABLE_COPY = Object.freeze({
    TAB_LABEL: 'Jira Tickets',
    TICKETS_TAB_LABEL: 'Support Tickets',

    /** Nothing at all for this FI — distinct from a filter that matched nothing. */
    EMPTY_ALL: 'No issues are being tracked for your institution yet.',
    /** A filter matched nothing. Never used for an integration failure. */
    EMPTY_FILTERED: 'No issues match this filter.',

    COLUMN_KEY: 'Key',
    COLUMN_SUMMARY: 'Summary',
    COLUMN_TYPE: 'Type',
    COLUMN_STATUS: 'Status',
    COLUMN_ASSIGNEE: 'Assignee',
    COLUMN_CREATED: 'Created',
    COLUMN_STARTED: 'Started',
    COLUMN_UPDATED: 'Last updated',

    UNASSIGNED: 'Unassigned',
    NOT_STARTED: 'Not started',
    /**
     * An unresolved issue. DateTimeCell's default for a null date is "Never",
     * which asserts the thing will not happen — wrong for work that is simply
     * still open.
     */
    NOT_RESOLVED: 'Not yet',
    /**
     * The issue's title is what makes a key mean something to an FI, so it is
     * shown everywhere an issue appears. It is nullable all the way down —
     * `jira_issue.summary` is `TEXT NULL` and the provider maps a missing Jira
     * title to null — so every render goes through `issueSummary()` below rather
     * than printing an empty string and leaving a bare key on screen.
     */
    NO_SUMMARY: 'Untitled issue',

    VIEW_TABLE: 'Table',
    VIEW_KANBAN: 'Board',
    VIEW_TOGGLE_LABEL: 'View',
});

/**
 * The Support screen's two tabs, in strip order.
 *
 * `requiredScope` is checked in ADDITION to SCOPES.SUPPORT_VIEW (the
 * Page-level gate) — see `issues.controller.js#getAccessibleSupportTabs`, which
 * filters this down to what the session can actually see.
 *
 * TICKETS HAS NO `requiredScope` ON PURPOSE, and the filter must treat that as
 * "always visible". Note this is the opposite of `ClockoutController.hasScope`,
 * which returns FALSE for a falsy key — filtering this array with a bare
 * `hasScope(tab.requiredScope)`, the way DASHBOARD_TABS and Treasury's TABS are
 * filtered, would hide the Support tab from everyone. Those two arrays get away
 * with it because every one of their tabs carries a scope; this one does not,
 * because reaching Support at all is what earns you the tickets.
 */
export const SUPPORT_TAB_ITEMS = Object.freeze([
    Object.freeze({
        key: SUPPORT_TABS.TICKETS,
        label: ISSUE_TABLE_COPY.TICKETS_TAB_LABEL,
        requiredScope: null,
    }),
    Object.freeze({
        key: SUPPORT_TABS.ISSUES,
        label: ISSUE_TABLE_COPY.TAB_LABEL,
        requiredScope: SCOPES.JIRA_TICKETS_VIEW,
    }),
]);

/**
 * The issue's title, or an explicit stand-in when the tracker has none.
 *
 * One helper for the four places an issue is displayed — the table cell, the
 * board card, the detail modal and the linked-issue card on a support ticket —
 * so a title-less issue reads the same way in all of them instead of each one
 * deciding independently whether to render nothing.
 *
 * @param {string | null | undefined} summary
 * @returns {string}
 */
export function issueSummary(summary) {
    const text = typeof summary === 'string' ? summary.trim() : '';
    return text || ISSUE_TABLE_COPY.NO_SUMMARY;
}

export const ISSUE_KANBAN_COPY = Object.freeze({
    /**
     * A column with no cards still renders, with this line. An absent column
     * would quietly redefine the workflow — "nothing is in review" and "there is
     * no review step" are different claims, and only the first one is ours.
     */
    EMPTY_COLUMN: 'Nothing here',
    /** The column's first page failed. Distinct from "this column is empty". */
    COLUMN_FAILED: 'Could not load this column.',
    LOADING_MORE: 'Loading more…',
    /** A later page failed; what is already scrolled in stays on screen. */
    MORE_FAILED: 'Could not load more. Scroll again to retry.',
    /** @param {number} n */
    countLabel: (n) => `${n} ${n === 1 ? 'issue' : 'issues'}`,
});

/**
 * How old the mirrored data is.
 *
 * Two states, not three. The age is always shown — "Updated 8 minutes ago" is
 * itself the honest report, and it is what the story asks for ("the age of the
 * data"). There is deliberately NO warning for a mirror that has not refreshed
 * recently: a five-minute sync drifting to fifteen is ordinary, and an amber
 * banner every time it does would train people to ignore the one state that
 * actually matters.
 *
 * That state is UNAVAILABLE — no successful sync has ever completed. It is an
 * error and it replaces the board, because an empty board reads as "nothing is
 * being worked on", which is the misreading this whole screen exists to prevent.
 */
export const ISSUE_SYNC_COPY = Object.freeze({
    FRESH_PREFIX: 'Updated',

    UNAVAILABLE_TITLE: 'Jira integration unavailable',
    UNAVAILABLE_DESCRIPTION:
        'We cannot reach Jira right now, so we cannot show what the team is working on. ' +
        'This is a connection problem, not an empty backlog. Please try again shortly.',

    /**
     * A THIRD state, and deliberately worded apart from the two above: the
     * request to our own API failed, so we know nothing at all — not that the
     * mirror is broken, and certainly not that the backlog is empty. It carries
     * the only retry on this screen, because unlike a mirror that has never run,
     * this one usually succeeds on a second try.
     */
    FAILED_TITLE: 'We could not load the issues',
    FAILED_DESCRIPTION:
        'The request did not complete, so we cannot say what the team is working on. ' +
        'This is not an empty backlog.',
    FAILED_RETRY: 'Try again',
});

export const ISSUE_PANEL_COPY = Object.freeze({
    // Key, summary, type and status are rendered in the modal HEADER and the
    // summary line, which carry no field label — so no copy for them here.
    FIELD_STAGE: 'Stage',
    FIELD_ASSIGNEE: 'Assignee',
    FIELD_PRIORITY: 'Priority',
    FIELD_CREATED: 'Created',
    FIELD_STARTED: 'Started',
    FIELD_UPDATED: 'Last updated',
    FIELD_RESOLVED: 'Resolved',

    /**
     * The tickets this FI raised that produced this issue. Plural because many
     * tickets to one issue is the mechanism that stops the same request being
     * worked twice — the FI should see all of its own, and only its own.
     */
    TICKETS_TITLE: 'Your support tickets',
    TICKETS_NONE: 'No support ticket from your institution is linked to this issue.',

    LOADING: 'Loading issue…',
    LOAD_FAILED: 'We could not load this issue. Please close and try again.',
});

export const TICKET_JIRA_LINK_COPY = Object.freeze({
    TITLE: 'Jira issue',
    /** A link whose issue is no longer publishable to this FI. The KEY is kept. */
    UNAVAILABLE: 'No longer available',
    UNAVAILABLE_HINT:
        'This issue is no longer visible to your institution. The reference is kept so the history stays traceable.',
});
